import _ from 'lodash';
import moment from 'moment';
import { browserHistory } from 'react-router';
import { badgeNotify, messageIntercom } from 'barnyard/utils';
import * as constants from '~/app/vet/core/constants/alerts';
import track, { analyticsTypes } from '~/app/vet/core/analytics';

const timestampFormat = 'YYYY-MM-DDTHH:mm:ss';
const notify = badgeNotify('bottom-left', 3);
const ONE_DAY_SECONDS = 60 * 60 * 24;

export const getDurationForAlert = (alert) => {
  // This helper function will reconcile the alert timeout and the alert expiration
  // into one value (in seconds) that can be passed to the alert duration

  // if there's no timeout or expiration, we return null to render the alert
  // indefinitely until it is manually dismissed
  if (_.isNull(alert.timeout) && _.isNull(alert.expiration)) {
    return null;
  }

  // convert the timeout and the expiration to their respective number of seconds in the future
  const secondsToTimeout = alert.timeout ? alert.timeout : Infinity;
  const secondsToExpiration = alert.expiration
    ? moment.duration(moment(alert.expiration, timestampFormat).diff(moment.utc())).asSeconds()
    : Infinity;

  // whichever comes first is the one we'll use
  const duration = Math.min(secondsToTimeout, secondsToExpiration);

  // timeouts have a max duration that they can be 32-bit signed integer
  // so anything over that will be called immediately. To address that,
  // if the duration is any longer than 1 day, let's not set a timeout
  // because the odds that we even hit that timeout are pretty low (we require
  // a refresh of the page with 6 hours of inactivity anyway)
  if (duration >= ONE_DAY_SECONDS) {
    return null;
  }

  return duration;
};

const buildBadgeFromAlert = (alert, dismissAction) => {
  const duration = getDurationForAlert(alert);
  if (duration < 0) {
    // This should never happen, but if it does we want to make sure we don't
    // build the alert because negative durations cause funky behavior
    return;
  }
  const alertBody = {
    key: alert.id,
    message: alert.content,
    isMarkdown: true,
    closable: alert.is_dismissible,
    onClose: () => dismissAction(alert.id),
    showIcon: false,
    confetti: alert.level === constants.ALERT_CELEBRATION,
    duration
  };

  // If we are going to stay within the vetcove app, we should
  // make sure that we use react-router
  if (_.startsWith(alert.action_link, '/react') || _.startsWith(alert.action_link, '/corporate')) {
    alertBody.onClick = () => {
      browserHistory.push(alert.action_link);
      dismissAction(alert.id);
      notify.remove(alert.id);
      track(analyticsTypes.ALERT_ACTION_LINK_CLICK, alert);
    };
  } else if (_.startsWith(alert.action_link, constants.INTERCOM_LINK)) {
    // If our link indicates that we want to open an Intercom chat,
    // read the message passed in the URL params or provide the alert's content.
    alertBody.onClick = () => {
      const message = new URLSearchParams(
        alert.action_link.replace(constants.INTERCOM_LINK, '')
      ).get('message');
      const defaultMessage = `I received this notification: "${alert.content}"`;
      messageIntercom(message || defaultMessage);
      dismissAction(alert.id);
      notify.remove(alert.id);
      track(analyticsTypes.ALERT_ACTION_LINK_CLICK, alert);
    };
  } else if (!_.isEmpty(alert.action_link)) {
    alertBody.link = alert.action_link;
    alertBody.onClick = () => {
      dismissAction(alert.id);
      notify.remove(alert.id);
    };
  }

  switch (alert.level) {
    case constants.ALERT_INFO:
      notify.info(alertBody);
      break;
    case constants.ALERT_WARNING:
      notify.warning(alertBody);
      break;
    case constants.ALERT_DANGER:
      notify.danger(alertBody);
      break;
    case constants.ALERT_SUCCESS:
    case constants.ALERT_CELEBRATION:
      notify.success(alertBody);
      break;
    default:
      break;
  }
};

const getSnackbarAlerts = alerts => _.filter(alerts, a => a.display === constants.SNACKBAR);

export const renderSnackbarAlerts = (alerts, dismiss) => _.each(
  getSnackbarAlerts(alerts), a => buildBadgeFromAlert(a, dismiss)
);

export const removeSnackbarAlerts = alerts => _.each(
  getSnackbarAlerts(alerts), a => notify.remove(a.id)
);
